import { SanityImageObject } from '@sanity/image-url/lib/types/types';
import { SanityDocument } from '@sanity/client';
import { SanityAsset } from '@sanity/image-url/lib/types/types';
import { VideoSource } from '../../components/landingPage/Video';

// eslint-disable-next-line
export type SanityObject<P extends Record<string, unknown> = any> = P & {
    _id: string;
    _type: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SanityArrayItem<P extends Record<string, unknown> = any> = P & {
    _key: string;
    _type: string;
};

export type SanityObjectWithout<
    P extends Record<string, unknown>,
    E extends keyof P
> = Exclude<P, E> & {
    _id: string;
    _type: string;
} & {
    [Prop in E]: P[Prop] | undefined;
};

export type SanityImage = SanityImageObject;

export type SanityBlock = SanityObject;

export type SanitySlug<S extends string = string> = {
    _type: 'slug';
    current: S;
};

export type SanityIcon = SanityObject<{
    slug: string;
    variant: 'regular' | 'solid' | 'light' | 'logoType';
}>;

export const datasets = ['landing_page', 'blog', 'app', 'dev'] as const;

export type Dataset = (typeof datasets)[number];

export type RentalFeature =
    | 'rental-explain'
    | 'rental-kyc'
    | 'rental-onboarding-black'
    | 'rental-onboarding-lite'
    | 'rental-onboarding-essential'
    | 'rental-prime-onboarding'
    | 'rental-free-trial-subscription'
    | 'rental-onboarding-free-trial'
    | 'rental-onboarding-free-trial-no-rental-feature'
    | 'rental-subscription'
    | 'rental-onboarding-out-trial'
    | 'rental-onboarding-old-subscription'
    | 'rent-advance-accredit'
    | 'rent-advance-explain';

export type ScreeningsFeature =
    | 'screenings'
    | 'screenings-onboarding'
    | 'screening-onboarding-user'
    | 'screening-onboarding-old-user'
    | 'screening-onboarding-subscription';

export type CapFeature = 'cap-onboarding' | 'cap';

export type Feature =
    | RentalFeature
    | ScreeningsFeature
    | CapFeature
    | 'contracts'
    | 'subscription'
    | 'pricing'
    | 'miles'
    | 'insurances'
    | 'listings';

export type FeatureFaq =
    | 'cap'
    | 'referral'
    | 'primePlan'
    | 'blackPlan'
    | 'basicPlan'
    | 'upgradeCard'
    | 'upgradeBoleto';

export type SanityFaqItem = {
    question: SanityBlock;
    answer: SanityBlock;
};

export type SanityFaqResponse = {
    questions: SanityFaqItem[];
};

export type TrainingContentVideoType = SanityDocument<{
    _type: 'video';
    slug: SanitySlug<string>;
    poster?: TrainingContentImageType;
    sources: VideoSource[];
}>;

export type TrainingContentBlockType = SanityArrayItem<{
    _type: 'block';
    style: string;
    markDefs: Record<string, unknown>[];
    children: Record<string, unknown>[];
}>;

export type TrainingContentImageType = SanityDocument<{
    _type: 'img';
    alt: string;
    caption: string;
    slug: SanitySlug<string>;
    image: {
        asset: SanityDocument<SanityAsset>;
        _type: 'image';
    };
}>;

export type TrainingContentType = {
    title: string;
    slug: SanitySlug<string>;
    content: (
        | TrainingContentBlockType
        | TrainingContentImageType
        | TrainingContentVideoType
    )[];
};

export type TrainingMenuItemType = SanityArrayItem<{
    title: string;
    content: SanityDocument<TrainingContentType>;
    children: TrainingMenuItemType[];
}>;

export type TrainingMenuType = SanityArrayItem<{
    title: string;
    items: TrainingMenuItemType[];
}>;

export type TrainingDataType = SanityDocument<{
    title: string;
    slug: SanitySlug<string>;
    items: TrainingMenuType[];
}>;

export type TrainingItemPath = { title: string; slug?: string };
